import { MainLayoutContainer } from "components/Layout";
import { Add, FileUpload } from "@mui/icons-material";
import { REQUIREMENT, TABLE_ACTIONS } from "helpers/constants";
import { Link, useNavigate } from "react-router-dom";
import { useRequirement } from "pages/Requirements/list/useRequirement";
import CustomTable from "components/Table/CustomTable";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import { AdminRoutes } from "config/routes";
import { ResponsiveButton, ResponsiveButtonLink } from "components/Button";
import { Link as LinkBase, Stack } from "@mui/material";
import { AddRequirementSelector } from "./components";
import { useAuthUser } from "hooks/useAuthUser";
import { ROLES } from "types/user";
import { DomainColorSquares } from "components/Requirements";
import { useMemo } from "react";
import { requirementsHaveDomainSwatches } from "utils/requirementDomains";

export const CompetencyList = () => {
  const {
    allRequirements,
    loading,
    deleteRequirementOpen,
    isOrgView,
    organizationId,
    requirements,
    handleActionClick,
    handleAddSelectedRequirement,
    setDeleteRequirementOpen,
    handleDelete,
  } = useRequirement(REQUIREMENT.REQUIREMENT_COMPETENCY);

  const navigate = useNavigate();
  const { role } = useAuthUser();
  const isAdmin = role === ROLES.ADMIN;
  const showDomainsColumn = useMemo(
    () => requirementsHaveDomainSwatches(requirements),
    [requirements],
  );
  return (
    <MainLayoutContainer
      title="Curriculum Requirements - Competencies"
      loading={loading}
      error={undefined}
      buttonActions={
        <Stack direction="row" spacing={2}>
          {isOrgView && (
            <AddRequirementSelector
              requirements={allRequirements}
              onSelect={(requirement) => {
                handleAddSelectedRequirement(requirement);
              }}
              selectedRequirement={requirements}
            />
          )}
          {isAdmin && (
            <ResponsiveButtonLink
              to={
                isOrgView && organizationId
                  ? AdminRoutes.OrganizationUploadSpreadsheetFramework.replace(
                      ":organizationId",
                      organizationId,
                    )
                  : AdminRoutes.UploadSpreadsheetFramework
              }
              icon={<FileUpload />}
            >
              Import spreadsheet
            </ResponsiveButtonLink>
          )}
          <ResponsiveButton
            onClick={() => {
              navigate(
                organizationId
                  ? `/organization/${organizationId}/competency/create`
                  : AdminRoutes.CompetenciesCreate,
              );
            }}
            icon={<Add />}
          >
            Create New Competency
          </ResponsiveButton>
        </Stack>
      }
    >
      <CustomTable
        entities={requirements}
        handleActionClick={handleActionClick}
        fieldToFilter={(row: any, filterText: string) =>
          (row.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
          (row.overview || "").toLowerCase().includes(filterText.toLowerCase())
        }
        enableCardViewInSmallDevices
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "overview",
            label: "Requirement Overview",
            value: (row: any) => {
              return row.overview?.length > 128
                ? row.overview?.slice(0, 128)
                : row.overview;
            },
          },
          ...(showDomainsColumn
            ? [
                {
                  id: "domains",
                  label: "Domains",
                  value: (row: any) => (
                    <DomainColorSquares requirement={row} />
                  ),
                },
              ]
            : []),
          {
            id: "requirement_id",
            value: (row: any) => (
              <Stack direction="row" spacing={2} alignItems={"center"}>
                <LinkBase
                  sx={{ cursor: "pointer" }}
                  component={Link}
                  to={
                    organizationId
                      ? `/organization/${organizationId}/competency/${row.requirement_id}`
                      : `/competency/${row.requirement_id}`
                  }
                >
                  View
                </LinkBase>
              </Stack>
            ),
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[
          TABLE_ACTIONS.VIEW,
          TABLE_ACTIONS.EDIT,
          TABLE_ACTIONS.DELETE,
        ]}
        extraActionItems={[]}
        idColumn={"requirement_id"}
      />
      {deleteRequirementOpen && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={`Are you sure you want to ${
            isOrgView ? "remove" : "delete"
          } this Requirement?`}
          show={deleteRequirementOpen}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteRequirementOpen(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
};
